// 配信ページ(設計書 §3.4.4)
// 現在のセッション状態によって自動で「開始フォーム」と「配信中ビュー」を切り替える。
// CaptureSettings はここで保持し、両画面で同じデバイス/制約を使う。
// VDO Ninja 経由の WebRTC 実送出は Phase 5(VDO セルフホスト基盤)と組み合わせて実装。
//
// `useCurrentSession` は 404 を「配信中セッションなし」として `data: null` に
// 正規化するため、ここでは error 分岐に 404 を考慮する必要はない。それ以外の
// 5xx / ネットワークエラーは ErrorBanner として表示しつつ、開始フォームは
// そのまま表示する(再試行を妨げないため)。

import { useState } from 'react';
import { useCurrentSession } from '../api/sessions';
import { ApiClientError } from '../api/client';
import { AppLayout } from '../components/AppLayout';
import { Card } from '../components/ui/Card';
import { DEFAULT_CAPTURE_SETTINGS, type CaptureSettings } from '../lib/media-capture';
import { LiveSessionView } from './streaming/LiveSessionView';
import { StartSessionForm, type VdoSessionParams } from './streaming/StartSessionForm';

export function StreamingPage() {
  const currentQuery = useCurrentSession();
  const [settings, setSettings] = useState<CaptureSettings>(DEFAULT_CAPTURE_SETTINGS);
  // createSession のレスポンスから得た VDO Ninja 接続情報。
  // ブラウザリロード後は失われる(セッション継続はできるが iframe を再構築不可、
  // ユーザーには「停止 → 再開」を促す)。
  const [vdoParams, setVdoParams] = useState<VdoSessionParams | null>(null);

  function patchSettings(patch: Partial<CaptureSettings>) {
    setSettings((prev) => ({ ...prev, ...patch }));
  }

  if (currentQuery.isLoading) {
    return (
      <AppLayout title="配信" description="読み込み中…">
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            セッション状態を取得しています…
          </p>
        </Card>
      </AppLayout>
    );
  }

  // 配信中セッションあり → ライブ画面
  if (currentQuery.data) {
    return (
      <LiveSessionView
        session={currentQuery.data}
        settings={settings}
        vdoParams={vdoParams}
        onSessionEnded={() => setVdoParams(null)}
      />
    );
  }

  // currentQuery.data === null かつ error あり = 5xx 等の真エラー。
  // フォーム自体は表示しつつ、上部にバナーで通知する。
  // currentQuery.data === null かつ error なし = 配信なし(正常)。
  return (
    <StartSessionForm
      settings={settings}
      onSettingsChange={patchSettings}
      onSessionCreated={setVdoParams}
      sessionFetchError={fetchErrorMessage(currentQuery.error)}
    />
  );
}

// useCurrentSession の error を表示用文字列に整形する。
// 404 は queryFn で null に正規化済みなのでここには来ない。
function fetchErrorMessage(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiClientError) {
    // 5xx 等。message は API が用意した日本語メッセージなのでそのまま使う。
    return `セッション状態の取得に失敗しました(${error.message})。再読み込みするか、しばらく待って再度お試しください。`;
  }
  return 'セッション状態の取得に失敗しました。再読み込みするか、しばらく待って再度お試しください。';
}
